import React, { ReactNode } from "react";
import { Container } from "@material-ui/core";
import ScrollToTopOnMount from "../ScrollToTopOnMount";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import WithLoadingSpinners from "../hoc/WithLoadingSpinner";
import Markdown from "./Markdown";
import fromHtml from "../../functions/fromHtml";
import styled from "styled-components";
import ITocItem from "../../models/ITocItem";
import { IApplicationState } from "../../store";
import { selectAuth } from "../../store/auth/selectors";
import WithSideMenu, { IDocContext } from "./WithSideMenu";
import getLocationInfo from "../../functions/getLocationInfo";
import Fetch from "../../services/Fetch";

const DocContainer = styled(Container)`
  padding-top: 3em;
  padding-bottom: 3em;
`;

interface IDocProps extends RouteComponentProps<{ type?: string; section?: string }> {
  token?: string;
}

interface IDocResult {
  content?: ReactNode;
  toc?: string;
  tocTree?: ITocItem;
  name: string;
  isDefault: boolean;
}

function Docs(props: IDocProps) {
  let [error, setError] = React.useState<string | undefined>(undefined);
  let [doc, setDoc] = React.useState<IDocResult>({
    name: "",
    isDefault: false,
  });

  const docContext: IDocContext = {
    id: doc.toc,
    toc: doc.tocTree,
    doc: doc.name,
  };

  const { location, token } = props;
  const { location: search } = props;

  React.useEffect(() => {
    setError(undefined);
    const refreshFromApi = async (toggleSideMenu: boolean) => {
      try {
        if (!token) {
          throw new Error("Unable to load: Unauthorized");
        }
        const info = getLocationInfo(location);
        if (!info.doc) {
          throw new Error("Unable to parse document location");
        }

        const resp = await Fetch(info.doc.remoteUrl, undefined, token);
        const text = await resp.text();
        if (!resp.ok) {
          throw new Error(`${resp.status} - ${resp.statusText || "Unknown error"}`);
        }

        const { node, toc, tocTree } = fromHtml(text, info.doc.id);

        setDoc({
          content: node,
          toc: toc,
          tocTree: tocTree,
          name: info.doc.id,
          isDefault: info.doc.isDefault,
        });
      } catch (error) {
        setError(error);
      }
    };
    refreshFromApi(false);
  }, [search]);

  React.useEffect(() => {
    setTimeout(() => {
      if (!window.location.hash) {
        window.scrollTo(0, 0);
      } else {
        const id = window.location.hash.replace("#", "");
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }, 500);
  }, [props.location.search]);

  return (
    <WithSideMenu docContext={docContext}>
      <WithLoadingSpinners isLoading={!doc.content} message="Fetching doc..." error={error} setError={setError}>
        <ScrollToTopOnMount />
        <DocContainer>
          <Markdown content={doc.content} />
        </DocContainer>
      </WithLoadingSpinners>
    </WithSideMenu>
  );
}

export default connect((state: IApplicationState) => {
  return selectAuth(state);
})(Docs);
